import { V2_FOOD_ICON_KEYS } from "./v2Schema";
import { calculateNutrientsForQuantity } from "./v2FoodLogUtils";
import {
  getFoodIconKey,
  getSafeQuantityUnit,
  INVALID_QUANTITY_UNIT_MESSAGE,
  isValidQuantityUnit,
  toFoodNumber,
} from "./v2FoodUtils";

const NUTRIENT_FIELDS = Object.freeze(["potassium_mg", "phosphorus_mg", "sodium_mg", "protein_g"]);

export function validateMixerQuantity(quantity) {
  const parsed = Number(quantity);
  if (!String(quantity ?? "").trim() || !Number.isFinite(parsed) || parsed <= 0) {
    return { valid: false, error: "Quantity must be greater than 0." };
  }

  return { valid: true, error: "", quantity: parsed };
}

export function createFoodIngredient(food, quantity) {
  const validation = validateMixerQuantity(quantity);
  if (!validation.valid) {
    return { ok: false, errors: { quantity: validation.error }, ingredient: null };
  }

  if (!food?.id) {
    return { ok: false, errors: { food: "Select a food first." }, ingredient: null };
  }

  const nutrients = scaleFoodNutrients(food, validation.quantity);

  return {
    ok: true,
    errors: {},
    ingredient: {
      id: `${food.type || "food"}-${food.id}-${Date.now()}`,
      food_id: food.id,
      food_type: food.type || "verified",
      name: food.display_name || food.name_en || food.name || "",
      category: food.category || "Custom",
      quantity: validation.quantity,
      quantity_unit: getSafeQuantityUnit(food.quantity_unit || food.serving_unit, "serving"),
      source: food.source || (food.type === "custom" ? "User custom food" : "Local food list"),
      icon_key: getFoodIconKey(food.icon_key || V2_FOOD_ICON_KEYS.default),
      ...nutrients,
    },
  };
}

export function validateManualIngredient(input = {}) {
  const errors = {};

  if (!String(input.name || "").trim()) {
    errors.name = "Ingredient name is required.";
  }

  if (!isValidQuantityUnit(input.quantity_unit)) {
    errors.quantity_unit = INVALID_QUANTITY_UNIT_MESSAGE;
  }

  validatePositive(input.quantity, "quantity", "Quantity must be greater than 0.", errors);
  validateZeroOrPositive(input.potassium_mg, "potassium_mg", "Potassium must be 0 or greater.", errors);
  validateZeroOrPositive(input.phosphorus_mg, "phosphorus_mg", "Phosphorus must be 0 or greater.", errors);
  validateZeroOrPositive(input.sodium_mg, "sodium_mg", "Sodium must be 0 or greater.", errors);
  validateZeroOrPositive(input.protein_g, "protein_g", "Protein must be 0 or greater.", errors);

  return {
    valid: Object.keys(errors).length === 0,
    errors,
  };
}

export function createManualIngredient(input = {}) {
  const validation = validateManualIngredient(input);
  if (!validation.valid) {
    return { ok: false, errors: validation.errors, ingredient: null };
  }

  return {
    ok: true,
    errors: {},
    ingredient: {
      id: `manual-${Date.now()}`,
      food_id: "",
      food_type: "manual",
      name: String(input.name).trim(),
      category: String(input.category || "Measured").trim() || "Measured",
      quantity: toFoodNumber(input.quantity),
      quantity_unit: getSafeQuantityUnit(input.quantity_unit, "serving"),
      potassium_mg: roundNutrient(toFoodNumber(input.potassium_mg)),
      phosphorus_mg: roundNutrient(toFoodNumber(input.phosphorus_mg)),
      sodium_mg: roundNutrient(toFoodNumber(input.sodium_mg)),
      protein_g: roundNutrient(toFoodNumber(input.protein_g)),
      source: "Measured ingredient",
      icon_key: getFoodIconKey(input.icon_key || V2_FOOD_ICON_KEYS.manual),
    },
  };
}

export function scaleFoodNutrients(food = {}, quantity = 0) {
  const nutrients = calculateNutrientsForQuantity(food, quantity);

  return {
    potassium_mg: nutrients.potassium_mg_snapshot,
    phosphorus_mg: nutrients.phosphorus_mg_snapshot,
    sodium_mg: nutrients.sodium_mg_snapshot,
    protein_g: nutrients.protein_g_snapshot,
  };
}

export function calculateMixerTotals(ingredients = []) {
  return ingredients.reduce(
    (totals, ingredient) => {
      NUTRIENT_FIELDS.forEach((field) => {
        totals[field] = roundNutrient(totals[field] + toFoodNumber(ingredient[field]));
      });
      return totals;
    },
    {
      potassium_mg: 0,
      phosphorus_mg: 0,
      sodium_mg: 0,
      protein_g: 0,
    }
  );
}

export function buildCustomFoodFromMixer(form = {}, ingredients = []) {
  const totals = calculateMixerTotals(ingredients);

  return {
    name: form.name,
    name_ar: form.name_ar || "",
    category: form.category || "Mixed meal",
    quantity: form.quantity,
    quantity_unit: getSafeQuantityUnit(form.quantity_unit, "serving"),
    potassium_mg: totals.potassium_mg,
    phosphorus_mg: totals.phosphorus_mg,
    sodium_mg: totals.sodium_mg,
    protein_g: totals.protein_g,
    icon_key: getFoodIconKey(form.icon_key || V2_FOOD_ICON_KEYS.meal),
    source: "Mixer calculation",
    mixer_ingredients: ingredients.map((ingredient) => ({
      id: ingredient.food_id || ingredient.id,
      name: ingredient.name,
      quantity: ingredient.quantity,
      quantity_unit: getSafeQuantityUnit(ingredient.quantity_unit, "serving"),
      potassium_mg: ingredient.potassium_mg,
      phosphorus_mg: ingredient.phosphorus_mg,
      sodium_mg: ingredient.sodium_mg,
      protein_g: ingredient.protein_g,
      source: ingredient.source,
    })),
  };
}

function validatePositive(value, key, message, errors) {
  const parsed = Number(value);
  if (!String(value ?? "").trim() || !Number.isFinite(parsed) || parsed <= 0) {
    errors[key] = message;
  }
}

function validateZeroOrPositive(value, key, message, errors) {
  const parsed = Number(value);
  if (!String(value ?? "").trim() || !Number.isFinite(parsed) || parsed < 0) {
    errors[key] = message;
  }
}

function roundNutrient(value) {
  return Math.round(toFoodNumber(value) * 10) / 10;
}
